import {
  CompanyEntity,
  FlowAllocationType,
  getObjectId,
  toFlowAllocationType,
  toTransportCategory,
  TransportCategory
} from "@ots-frontend/ots-data";
import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Modal, ModalProps, Switch, TextInput} from "@mantine/core";
import {useCallback} from "react";
import {FlowAllocationTypeSelect} from "../../select/entity/FlowAllocationTypeSelect/FlowAllocationTypeSelect";
import {DateInput} from "../../date/DateInput/DateInput";
import {TransportCategorySelect} from "../../select/entity/TransportCategorySelect/TransportCategorySelect";
import {
  FreightTypeCategoryAutocomplete
} from "../../autocomplete/entity/FreightTypeAutocomplete/FreightTypeCategoryAutocomplete";
import {CompanySelect} from "../../select/entity/CompanySelect/CompanySelect";

interface PipelineFormProps {
  options?: PipelineFormOptions;
  defaultValues?: Partial<PipelineFormValues>;
  onSubmit?: (values: PipelineFormValues) => void;
  submitting?: boolean;
}

export interface PipelineFormOptions {
  // props here
}

export interface PipelineFormValues {
  name: string
  company: CompanyEntity | string
  category: TransportCategory
  freightTypeCategory?: string
  allocationType: FlowAllocationType
  inactiveDate?: Date
  temporary?: boolean
  doNotUse?: boolean
}

const getDefaultValues = (defaultValues: Partial<PipelineFormValues> | undefined) => {
  return {
    name: defaultValues?.name,
    company: getObjectId(defaultValues?.company),
    allocationType: defaultValues?.allocationType?.code,
    category: defaultValues?.category?.code,
    freightTypeCategory: defaultValues?.freightTypeCategory,
    inactiveDate: defaultValues?.inactiveDate,
    temporary: defaultValues?.temporary,
    doNotUse: defaultValues?.doNotUse,
  }
}

export const PipelineForm = (props: PipelineFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useForm({
    initialValues: getDefaultValues(defaultValues),
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        name: values.name!,
        company: values.company!,
        category: toTransportCategory(values.category!),
        freightTypeCategory: values.freightTypeCategory,
        allocationType: toFlowAllocationType(values.allocationType!),
        inactiveDate: values.inactiveDate,
        temporary: values.temporary,
        doNotUse: values.doNotUse,
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <TextInput
        label={t("common.entity.pipeline.form.field.name", "Name")}
        placeholder={t<string>("common.entity.pipeline.form.field.name.placeholder", "Name")}
        styles={{wrapper: {width: "100%"}}}
        withAsterisk
        required
        {...form.getInputProps("name")} />
      <Divider my="sm" variant="dotted"/>
      <CompanySelect
        label={t("common.entity.pipeline.form.field.company", "Company")}
        placeholder={t<string>("common.entity.pipeline.form.field.type.company", "Company")}
        styles={{wrapper: {width: "100%"}}}
        withAsterisk
        required
        clearable
        {...form.getInputProps("company")} />
      <Divider my="sm" variant="dotted"/>
      <TransportCategorySelect
        label={t("common.entity.pipeline.form.field.category", "Category")}
        placeholder={t<string>("common.entity.pipeline.form.field.type.category", "Category")}
        styles={{wrapper: {width: "100%"}}}
        withAsterisk
        required
        clearable
        {...form.getInputProps("category")} />
      <FreightTypeCategoryAutocomplete
        label={t("common.entity.pipeline.form.field.freighttypecategory", "Sub Category")}
        placeholder={t<string>("common.entity.pipeline.form.field.type.freighttypecategory", "Sub Category")}
        styles={{wrapper: {width: "100%"}}}
        {...form.getInputProps("freightTypeCategory")} />
      <Divider my="sm" variant="dotted"/>
      <FlowAllocationTypeSelect
        label={t("common.entity.pipeline.form.field.allocation", "Allocation")}
        placeholder={t<string>("common.entity.pipeline.form.field.type.allocation", "Allocation")}
        styles={{wrapper: {width: "100%"}}}
        withAsterisk
        required
        clearable
        {...form.getInputProps("allocationType")} />
      <Divider my="sm" variant="dotted"/>
      <DateInput
        valueFormat="MM/DD/YYYY HH:mm"
        label={t("common.entity.pipeline.form.field.inactivedate", "Inactive Date")}
        placeholder={t<string>("common.entity.pipeline.form.field.date.inactivedate", "Inactive Date")}
        styles={{wrapper: {width: "100%"}}}
        popoverProps={{
          withinPortal: true
        }}
        clearable
        {...form.getInputProps("inactiveDate")} />
      <Divider my="sm" variant="dotted"/>
      <Switch
        label={t("common.entity.pipeline.form.field.temporary", "Temporary")}
        placeholder={t<string>("common.entity.pipeline.form.field.temporary", "Temporary")}
        {...form.getInputProps("temporary", {type: 'checkbox'})} />
      <Divider my="sm" variant="dotted"/>
      <Switch
        label={t("common.entity.pipeline.form.field.donotuse", "Do Not Use")}
        placeholder={t<string>("common.entity.pipeline.form.field.donotuse", "Do Not Use")}
        {...form.getInputProps("doNotUse", {type: 'checkbox'})} />
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface PipelineFormModalProps extends PipelineFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const PipelineFormModal = (props: PipelineFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <PipelineForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
